/**
 * Land use: what the gaps between buildings actually are.
 *
 * Without this every space that is not a building is the same black hole, and
 * a park, a surface car park and a demolished block look identical. They are
 * not the same thing, and the difference is most of what makes a city block
 * feel the way it does.
 *
 * Parks are deliberately dark: a saturated green would out-shout the incident
 * beacons, which are the point of the map. Scattered tree marks give the
 * larger ones some texture without adding real geometry.
 */

import { PolygonLayer, ScatterplotLayer, SolidPolygonLayer } from "@deck.gl/layers";

import { pmTileLayer, tileFeatures } from "../map/tiles.js";

import { Layer } from "./base.js";
import { palette, themeName } from "../theme/palette.js";
import { OVER_EVERYTHING } from "../map/depth.js";

/**
 * Land use styling, resolved from the active palette.
 *
 * A function rather than a constant: the colours change with the theme, and a
 * map captured at import time would keep whichever theme was active on load.
 */
function styles() {
  const c = palette();
  return {
    park:         { fill: c.park, line: c.parkLine, label: "Park" },
    greenery:     { fill: c.greenery, line: c.greeneryLine, label: "Greenery" },
    parking:      { fill: c.parking, line: c.parkingLine, label: "Parking" },
    vacant:       { fill: c.vacant, line: c.vacantLine, label: "Vacant lot" },
    construction: { fill: c.construction, line: c.constructionLine, label: "Construction site" },
  };
}

/** Below this, verges are sub-pixel and only cost blending time. */
const GREENERY_MIN_ZOOM = 14.5;

/** Rough polygon area in m², via the shoelace formula at Denver's latitude. */
function ringArea(ring) {
  let sum = 0;
  for (let i = 0; i < ring.length - 1; i += 1) {
    sum += ring[i][0] * ring[i + 1][1] - ring[i + 1][0] * ring[i][1];
  }
  return Math.abs(sum / 2) * 85600 * 110540;
}

/** Centroid of a ring, for scattering trees around. */
function centroid(ring) {
  let x = 0;
  let y = 0;
  for (let i = 0; i < ring.length - 1; i += 1) {
    x += ring[i][0];
    y += ring[i][1];
  }
  const n = Math.max(1, ring.length - 1);
  return [x / n, y / n];
}

export class LandUseLayer extends Layer {
  static key = "landuse";
  static label = "Land use";
  static description = "Parks, parking, vacant lots and construction";

  async load() {
    if (this.tiled) {
      // Nothing to fetch: the geometry arrives tile by tile as the map moves.
      this.status = "ready";
      this.detail = "tiled";
      return null;
    }
    this.status = "loading";
    try {
      const response = await fetch(`${this.basePath}/landuse`);
      if (!response.ok) throw new Error(String(response.status));
      const body = await response.json();
      this._areas = (body.areas ?? []).filter((a) => a.ring?.length > 3);
      this._trees = this.scatterTrees(this._areas);
      this.status = this._areas.length ? "ready" : "unavailable";
      this.detail = `${this._areas.length} areas`;
      return null;
    } catch (error) {
      this.status = "unavailable";
      this.detail = `Unavailable (${error.message})`;
      return null;
    }
  }

  get loadedCount() {
    return this._areas?.length ?? 0;
  }

  /**
   * Deterministic pseudo-random tree positions inside larger parks.
   *
   * Seeded from the centroid so the same park always gets the same trees —
   * a park whose trees jump around on every repaint reads as a rendering bug.
   * Points are jittered within the ring's bounding box and kept only if they
   * fall inside it, which is cheap and good enough for decoration.
   */
  scatterTrees(areas) {
    const trees = [];
    for (const area of areas) {
      if (area.kind !== "park") continue;
      const size = ringArea(area.ring);
      if (size < 4000) continue;

      const [cx, cy] = centroid(area.ring);
      const xs = area.ring.map((p) => p[0]);
      const ys = area.ring.map((p) => p[1]);
      const [west, east] = [Math.min(...xs), Math.max(...xs)];
      const [south, north] = [Math.min(...ys), Math.max(...ys)];

      const count = Math.min(90, Math.round(size / 900));
      let seed = Math.abs(Math.round((cx + cy) * 1e6)) % 100000;
      const random = () => {
        seed = (seed * 1103515245 + 12345) % 2147483648;
        return seed / 2147483648;
      };

      for (let i = 0; i < count; i += 1) {
        const point = [west + (east - west) * random(), south + (north - south) * random()];
        if (pointInRing(point, area.ring)) trees.push({ p: point });
      }
    }
    return trees;
  }

  describe(area) {
    // Two shapes arrive here and only one used to be handled.
    //
    // From the API a land use area is flat — kind, name, access, ring. From a
    // tile it is a GeoJSON feature, with those under `properties` and the ring
    // under `geometry.coordinates`. Reading only the flat shape meant every
    // hover after the move to tiles found `kind` undefined, fell through to the
    // vacant-lot fallback, and asked ringArea for the area of nothing — which
    // is why a construction site stopped saying it was one.
    const props = area?.properties ?? area ?? {};
    const ring = area?.geometry?.coordinates?.[0] ?? area?.ring;

    const style = styles()[props.kind] ?? styles().vacant;
    const rows = [["Type", style.label]];
    if (props.access) rows.push(["Access", props.access]);
    if (Array.isArray(ring) && ring.length > 2) {
      rows.push(["Area", `~${Math.round(ringArea(ring)).toLocaleString()} m²`]);
    }
    rows.push(["Source", "OpenStreetMap"]);
    return { title: props.name || style.label, rows };
  }

  legend() {
    return Object.entries(styles())
      .filter(([kind]) => kind !== "greenery")
      .map(([kind, style]) => ({
        key: kind, rgb: style.fill.slice(0, 3), label: style.label,
      }));
  }

  /**
   * The greenery gate changes what render() returns, on both the tiled and
   * the whole-file path, so crossing it needs a feeds repaint — without this,
   * verges appeared only on the next full paint, up to a minute after zooming
   * in, which read as the layer loading forever.
   */
  get zoomThresholds() {
    return [GREENERY_MIN_ZOOM];
  }

  /** Where the tiled version of this data lives, if it has been built. */
  tileUrl = "./data/landuse.pmtiles";

  /**
   * Set by the app when the archive is present.
   *
   * The layer owns this rather than the caller branching around it: excluding
   * the layer from the load loop left it permanently "idle", which the HUD
   * renders as a dead toggle even though the tiles were drawing perfectly.
   */
  tiled = false;

  /**
   * Which basemap layer to draw beneath, or undefined to draw on top.
   *
   * Set by the app, and only ever to a layer that actually exists. These
   * polygons are ground cover drawn with depth testing off, so left on top
   * they paint straight over the road network — which is what made streets
   * vanish on crossing the greenery zoom.
   *
   * It belongs on the top-level layer, never on a tile sublayer: those are
   * created and destroyed as tiles come and go, and beforeId makes the overlay
   * insert into the MapLibre style, so a sublayer would churn the style on
   * every tile.
   */
  beforeId = undefined;

  /**
   * Tiled land use.
   *
   * Replaces a 5.8MB payload with the handful of tiles under the viewport.
   * The greenery gate stays — 21,077 of 23,017 areas are verges, and below
   * z14.5 they are sub-pixel and only cost blending time — but it is applied
   * per feature now rather than by filtering an array the client already has.
   */
  tileLayer(zoom = 16) {
    const style = styles();
    const showGreenery = zoom >= GREENERY_MIN_ZOOM;

    return pmTileLayer({
      id: `${this.key}-tiles`,
      url: this.tileUrl,
      beforeId: this.beforeId,
      visible: this.enabled,
      minZoom: 9,
      maxZoom: 14,
      // filterStamp joins the greenery gate and the theme in here for exactly
      // the same reason they are already here: renderSubLayers is a closure
      // deck caches per tile, so a tile already on screen would keep drawing
      // the kinds you just hid until something unrelated invalidated it.
      updateTriggers: {
        renderSubLayers:
          `${showGreenery}:${themeName()}:${this.filterStamp}`,
      },
      renderSubLayers: (props) => {
        const all = tileFeatures(props);
        if (!all?.length) return null;
        const data = this.visibleOf(
          showGreenery ? all : all.filter((f) => f.properties?.kind !== "greenery"),
          (f) => f.properties?.kind,
        );
        if (!data.length) return null;

        return new SolidPolygonLayer(props, {
          id: `${props.id}-fill`,
          data,
          getPolygon: (f) => f.geometry.coordinates,
          getFillColor: (f) => (style[f.properties?.kind] ?? style.vacant).fill,
          // The style closure above was captured when the parent was built;
          // the sublayer must be told when its world moved, because the
          // parent's renderSubLayers trigger means nothing to getFillColor.
          updateTriggers: { getFillColor: `${themeName()}:${this.filterStamp}` },
          filled: true,
          extruded: false,
          pickable: true,
          // Co-planar ground fill; see the note in render() about depth.
          parameters: OVER_EVERYTHING,
        });
      },
    });
  }

  render(now, zoom = 16) {
    if (!this.enabled || this.status !== "ready") {
      this.visibleCount = 0;
      return [];
    }
    if (this.tiled) return [this.tileLayer(zoom)];

    // Greenery is 21,077 of the 23,017 areas and each one is a translucent
    // polygon over the same ground. Blending all of them every frame is the
    // overdraw that made the map tear while panning — and zoomed out they are
    // individually smaller than a pixel, so nothing is lost by holding them
    // back until you are close enough to tell one from another.
    const areas = this.visibleOf(
      zoom >= GREENERY_MIN_ZOOM
        ? this._areas
        : this._areas.filter((a) => a.kind !== "greenery"),
      (a) => a.kind,
    );
    const trees = zoom >= GREENERY_MIN_ZOOM ? this._trees : [];
    const style = styles();
    this.visibleCount = areas.length;

    return [
      new PolygonLayer({
        id: `${this.key}-areas`,
        beforeId: this.beforeId,
        data: areas,
        getPolygon: (d) => d.ring,
        getFillColor: (d) => (style[d.kind] ?? style.vacant).fill,
        getLineColor: (d) => (style[d.kind] ?? style.vacant).line,
        getLineWidth: 1,
        lineWidthUnits: "pixels",
        stroked: true,
        filled: true,
        extruded: false,
        pickable: true,
        // Ground-level fill: it must sit under the buildings, not fight them.
        // depthMask off as well — these polygons are co-planar with each other
        // and with the basemap, and writing depth from them made overlapping
        // areas flicker as the camera moved and settle when it stopped.
        parameters: OVER_EVERYTHING,
      }),
      new ScatterplotLayer({
        id: `${this.key}-trees`,
        data: trees,
        getPosition: (d) => d.p,
        getRadius: 3.5,
        getFillColor: palette().tree,
        radiusUnits: "meters",
        radiusMinPixels: 0.6,
        radiusMaxPixels: 4,
        stroked: false,
        filled: true,
        pickable: false,
        parameters: OVER_EVERYTHING,
      }),
    ];
  }
}

/** Ray-casting point-in-polygon. */
function pointInRing([x, y], ring) {
  let inside = false;
  for (let i = 0, j = ring.length - 1; i < ring.length; j = i, i += 1) {
    const [xi, yi] = ring[i];
    const [xj, yj] = ring[j];
    if ((yi > y) !== (yj > y) && x < ((xj - xi) * (y - yi)) / (yj - yi) + xi) {
      inside = !inside;
    }
  }
  return inside;
}
